// Consulta para canjear un codigo de acceso de un solo uso emitido por el portal Zoco
// (POST /api/acceso/zoco) y entrar a Pagos sin volver a tipear la contraseña.
// React 18 StrictMode monta los efectos dos veces en desarrollo: sin la cache de abajo,
// ese segundo montaje repetiria el POST y quemaria el codigo de un solo uso compitiendo
// con la primera solicitud.

const RUTA_POR_DEFECTO = "/aliados/inicio";
const LARGO_MAXIMO_VOLVER = 2000;

// Cache de la ultima solicitud en curso (o ya resuelta) por codigo. No vence: un codigo
// de un solo uso nunca deberia disparar un segundo POST, este resuelto o no.
let solicitudPorCodigo = null;

// Resuelve volver con el mismo parser WHATWG URL que usa window.location.replace, en vez
// de chequear el prefijo del string crudo: ese parser descarta todo tab/CR/LF del input
// ANTES de parsear, asi que "/\t/evil.com" pasa un startsWith("/") ingenuo pero navegado
// es "//evil.com" (protocol-relative a otro origen). Para esquemas especiales (http/https)
// la barra invertida tambien vale como separador de autoridad, igual que "//". Comparar el
// origin de la URL ya resuelta -y usar esa URL normalizada como destino, nunca el string
// crudo- es la unica forma de que lo validado sea igual a lo que el navegador ejecuta.
const calcularDestino = (volver) => {
  if (typeof volver !== "string" || volver.length === 0 || volver.length > LARGO_MAXIMO_VOLVER) {
    return RUTA_POR_DEFECTO;
  }
  if (!volver.startsWith("/")) {
    return RUTA_POR_DEFECTO;
  }

  try {
    const url = new URL(volver, window.location.origin);
    if (url.origin !== window.location.origin) {
      return RUTA_POR_DEFECTO;
    }
    // Mismo origin no alcanza: "/..//evil.com" resuelve sus segmentos y el pathname queda
    // "//evil.com", que usado otra vez como destino relativo es protocol-relative a otro
    // origen. Normalizado ya no quedan tabs ni barras invertidas, asi que una sola barra
    // inicial garantiza que el destino no sale de este origen.
    if (url.pathname.startsWith("//")) {
      return RUTA_POR_DEFECTO;
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return RUTA_POR_DEFECTO;
  }
};

const interpretarRespuesta = async (response, destino) => {
  if (response.status === 403) {
    return { ok: false, motivo: "solo_dueno" };
  }

  if (!response.ok) {
    return { ok: false, motivo: "fallo" };
  }

  const data = await response.json().catch(() => null);
  if (!data || !data.token || data.rol !== 0) {
    return { ok: false, motivo: "fallo" };
  }

  return { ok: true, token: data.token, destino };
};

/**
 * Canjea un codigo de acceso de un solo uso contra POST /api/acceso/zoco.
 * Deduplica por codigo: mientras haya una solicitud en curso (o ya resuelta) para el mismo
 * codigo, se reutiliza la misma promesa y nunca se hace un segundo POST.
 */
export const entrarConCodigoZoco = (codigo, volver) => {
  const destino = calcularDestino(volver);

  if (!codigo) {
    return Promise.resolve({ ok: false, motivo: "fallo" });
  }

  if (solicitudPorCodigo && solicitudPorCodigo.codigo === codigo) {
    return solicitudPorCodigo.solicitud;
  }

  const url = process.env.REACT_APP_API_ACCESO_ZOCO;

  const solicitud = fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ codigo }),
  })
    .then((response) => interpretarRespuesta(response, destino))
    .catch(() => ({ ok: false, motivo: "fallo" }));

  solicitudPorCodigo = { codigo, solicitud };

  return solicitud;
};

/** Limpia la cache de solicitudes; se usa en los tests. */
export const reiniciarEntrarConCodigoZoco = () => {
  solicitudPorCodigo = null;
};
